/**
 * Audit panel for PlaybookDetail.
 * Shows alarm-events (incidents) for alarms attached to this playbook as a
 * proxy for "when did this playbook trigger". Labeled honestly — this is NOT
 * a real execution log (per-action execution history is a pending BE capability).
 */
import { useMemo } from 'react';
import { DataTable, EmptyState, StatusPill } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { Badge, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Siren } from 'lucide-react';
import { useAlarmEvents, type AlarmEvent } from '@/api/healthcheck';
import { eventStatusTone, severityTone, fmtRelTime } from '../shared';
import type { Alarm } from '@/api/healthcheck';

interface Props {
  /** Alarms currently attached to this playbook. */
  attachedAlarms: Alarm[];
  isLoadingAlarms: boolean;
}

export function PlaybookAuditPanel({ attachedAlarms, isLoadingAlarms }: Props) {
  // Fetch events for the first attached alarm_id as a combined proxy.
  // We use alarm_id filter per-alarm and merge. To keep it simple and avoid
  // N requests at mount, fetch recent events tenant-wide and filter client-side.
  const alarmIds = useMemo(
    () => new Set(attachedAlarms.map((a) => a.id)),
    [attachedAlarms],
  );

  const { data: eventsResult, isLoading: eventsLoading } = useAlarmEvents({
    page_size: 50,
  });

  const events = useMemo(() => {
    const allItems = eventsResult?.items ?? [];
    if (alarmIds.size === 0) return [];
    return allItems.filter((e) => alarmIds.has(e.alarm_id));
  }, [eventsResult, alarmIds]);

  const columns = useMemo<ColumnDef<AlarmEvent, unknown>[]>(
    () => [
      {
        id: 'when',
        header: 'When',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {fmtRelTime(row.original.triggered_at)}
          </span>
        ),
      },
      {
        id: 'alarm',
        header: 'Alarm',
        enableSorting: false,
        cell: ({ row }) => {
          const alarm = attachedAlarms.find((a) => a.id === row.original.alarm_id);
          return (
            <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
              {alarm?.name ?? row.original.alarm_name ?? row.original.alarm_id.slice(0, 8)}
            </span>
          );
        },
      },
      {
        id: 'severity',
        header: 'Severity',
        enableSorting: false,
        cell: ({ row }) => {
          const sev = row.original.severity;
          if (!sev) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
          return (
            <Badge tone={severityTone(sev)} appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>
              {sev}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={eventStatusTone(row.original.status)}>
            {row.original.status}
          </StatusPill>
        ),
      },
    ],
    [attachedAlarms],
  );

  const isLoading = isLoadingAlarms || eventsLoading;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Honesty note — always visible */}
      <Alert tone="info" title="Triggering incidents (proxy)">
        This shows incidents on the alarms attached to this playbook — not a literal
        execution log. Per-action execution history is a pending backend capability.
      </Alert>

      {isLoading ? (
        <Skeleton style={{ height: 120 }} />
      ) : (
        <DataTable
          columns={columns}
          data={events}
          getRowId={(e) => e.id}
          emptyState={
            <EmptyState
              icon={<Siren size={22} />}
              title="No triggering incidents yet"
              description={
                alarmIds.size === 0
                  ? 'Attach alarms to this playbook to see their incidents here.'
                  : 'No incidents have fired on the attached alarms yet.'
              }
            />
          }
        />
      )}
    </div>
  );
}
